import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { ArrowLeft, Video, FileText, FileSpreadsheet, Image } from 'lucide-react';

const ClassContent = () => {
  const { level, stream } = useParams();
  const [content, setContent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    const fetchContent = async () => {
      try {
        let url = `${API}/content?class_level=${level}`;
        if (stream) {
          url += `&stream=${stream}`;
        }
        const response = await axios.get(url);
        setContent(response.data);
      } catch (error) {
        console.error('Error fetching content:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchContent();
  }, [level, stream]);

  const filteredContent = filter === 'all' 
    ? content 
    : content.filter(c => c.content_type === filter);

  const contentTypes = [
    { id: 'all', label: 'All' },
    { id: 'video', label: 'Videos', icon: Video },
    { id: 'pdf', label: 'PDFs', icon: FileText },
    { id: 'ppt', label: 'PPTs', icon: FileSpreadsheet },
    { id: 'image', label: 'Images', icon: Image },
  ];

  const getIcon = (type) => {
    switch (type) {
      case 'video': return <Video className="w-8 h-8 text-red-500" />;
      case 'pdf': return <FileText className="w-8 h-8 text-blue-500" />;
      case 'ppt': return <FileSpreadsheet className="w-8 h-8 text-orange-500" />;
      case 'image': return <Image className="w-8 h-8 text-green-500" />;
      default: return <FileText className="w-8 h-8 text-zinc-500" />;
    }
  };

  const streamNames = {
    commerce: 'Commerce',
    science: 'Science',
    biology: 'Biology',
    humanities: 'Humanities',
  };

  return (
    <div className="pt-16 min-h-screen bg-[#050505]" data-testid="class-content-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <Link to="/classes" className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-4">
            <ArrowLeft className="w-4 h-4" />
            Back to Classes
          </Link>
          <h1 className="font-outfit text-4xl md:text-5xl font-black text-white">
            Class <span className="text-red-500">{level}th</span>
            {stream && (
              <span className="text-zinc-400 text-2xl md:text-3xl ml-3">
                / {streamNames[stream]}
              </span>
            )}
          </h1>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-8">
          {contentTypes.map((type) => (
            <Button
              key={type.id}
              variant={filter === type.id ? 'default' : 'outline'}
              onClick={() => setFilter(type.id)}
              className={`rounded-sm ${
                filter === type.id
                  ? 'bg-red-600 hover:bg-red-700 text-white'
                  : 'border-zinc-700 text-zinc-400 hover:text-white hover:bg-zinc-800'
              }`}
              data-testid={`filter-${type.id}`}
            >
              {type.icon && <type.icon className="w-4 h-4 mr-2" />}
              {type.label}
            </Button>
          ))}
        </div>

        {/* Content Grid */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-red-500"></div>
          </div>
        ) : filteredContent.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-zinc-500 text-lg">No content available yet.</p>
            <p className="text-zinc-600 mt-2">Check back soon for new materials!</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredContent.map((item, idx) => (
              <Link
                key={item.content_id}
                to={`/content/${item.content_id}`}
                className={`card-hover bg-[#121212] rounded-sm overflow-hidden animate-fadeIn stagger-${(idx % 5) + 1}`}
                data-testid={`content-card-${item.content_id}`}
              >
                <div className="h-40 bg-gradient-to-br from-zinc-800 to-zinc-900 flex items-center justify-center">
                  {getIcon(item.content_type)}
                </div>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`text-xs px-2 py-1 rounded-sm uppercase ${
                      item.content_type === 'video' ? 'bg-red-500/10 text-red-500' :
                      item.content_type === 'pdf' ? 'bg-blue-500/10 text-blue-500' :
                      item.content_type === 'ppt' ? 'bg-orange-500/10 text-orange-500' :
                      'bg-green-500/10 text-green-500'
                    }`}>
                      {item.content_type}
                    </span>
                    {item.is_paid && (
                      <span className="text-xs px-2 py-1 bg-yellow-500/10 text-yellow-500 rounded-sm">
                        ₹{item.price}
                      </span>
                    )}
                  </div>
                  <h3 className="font-semibold text-white mb-1 line-clamp-1">{item.title}</h3>
                  <p className="text-sm text-zinc-500 line-clamp-2">{item.description}</p>
                  <div className="mt-3 flex items-center gap-4 text-xs text-zinc-600">
                    <span>❤️ {item.likes_count || 0}</span>
                    <span>{new Date(item.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ClassContent;
